import React, {
  SyntheticEvent,
  useCallback,
  useContext,
  ReactNode,
  isValidElement,
  cloneElement,
  useState,
} from "react";
import {
  Button,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
} from "@material-ui/core";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import {
  Record,
  linkToRecord,
  useRefresh,
  useTranslate,
  DataProviderContext,
  Confirm,
} from "react-admin";
import { useHistory } from "react-router-dom";
import PageviewOutlinedIcon from "@material-ui/icons/PageviewOutlined";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import IndeterminateCheckBoxOutlinedIcon from "@material-ui/icons/IndeterminateCheckBoxOutlined";
import CheckBoxOutlinedIcon from "@material-ui/icons/CheckBoxOutlined";
import "./ActionsField.css";
import { ApiManager } from "../../../common/data/ApiManager";
import { RecordStatus } from "../../../common/data/service-proxies";
import { useDispatch } from "react-redux";
import { createCrudStatusChangedAction } from "../../../common/redux/actions/appData";
import inflection from "inflection";

interface IAdditionalActionsProps {
  record?: IProLogsRecord;
  menuOptions?: any;
  menuItemOptions?: any;
  basePath?: string;
  basePathOverride?: string;
  resource?: string;
  hasShow?: boolean;
  children?: ReactNode;
  confirmDeactivate?: boolean;
  confirmTitle?: string;
  confirmContent?: string;
  editable?: (record: any) => boolean;
  canActivateOrDeactivate?: (record: any) => boolean;
  viewLabel?: string;
}

export interface IProLogsRecord extends Record {
  status?: RecordStatus;
  editable?: boolean;
}

export const ActionsField = (props: IAdditionalActionsProps) => {
  const {
    record,
    resource,
    menuOptions,
    menuItemOptions,
    basePath,
    basePathOverride = basePath,
    hasShow,
    confirmDeactivate,
    confirmContent = "actions.confirmContent",
    confirmTitle = "actions.confirmTitle",
    editable,
    canActivateOrDeactivate,
    viewLabel = "actions.view",
    ...rest
  } = props;

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const history = useHistory();
  const dataProvider = useContext(DataProviderContext) as ApiManager;
  const refresh = useRefresh();
  const translate = useTranslate();
  const dispatch = useDispatch();
  const [isConfirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const activateRecord = useCallback(
    (record: IProLogsRecord) => {
      return dataProvider.changeStatus(resource ?? "unknown-resource", {
        id: record.id,
        status: RecordStatus.Active,
      });
    },
    [dataProvider, resource]
  );
  const deactivateRecord = useCallback(
    (record: IProLogsRecord) => {
      return dataProvider.changeStatus(resource ?? "unknown-resource", {
        id: record.id,
        status: RecordStatus.Inactive,
      });
    },
    [dataProvider, resource]
  );

  const isActivateOrDeactivateVisible = () => {
    if (!canActivateOrDeactivate) {
      return !!record?.status;
    }

    return canActivateOrDeactivate(record) && !!record?.status;
  };

  const handleAnchorClick = (event: SyntheticEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (func: (arg: any) => void) => (arg: any) => {
    handleMenuClose();
    if (typeof func === "function") {
      func(arg);
    }
  };

  const hasAnyItems = () => {
    return (
      isEditable() ||
      isActivateOrDeactivateVisible() ||
      (props.children &&
        React.Children.map(props.children, (c) => {
          if (!isValidElement(c)) {
            return;
          }

          if (!c.props.hidden) {
            return c;
          }

          if (!c.props.hidden(record)) {
            return c;
          }
        })?.length)
    );
  };

  const isEditable = () => {
    if (editable) {
      return editable(record);
    }

    if (typeof record?.editable !== "undefined") {
      return record.editable;
    }

    return true;
  };

  if (!record || !resource) {
    return null;
  }

  if (!hasAnyItems()) {
    return null;
  }

  return (
    <div>
      <Button
        {...sanitizeRestProps(rest)}
        variant="text"
        onClick={handleAnchorClick}
      >
        <MoreHorizIcon />
      </Button>
      <Menu
        {...menuOptions}
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {hasShow && (
          <MenuItem
            className="actions-menu-item"
            {...menuItemOptions}
            onClick={handleMenuItemClick(() => {
              history.push(linkToRecord(basePathOverride, record.id, "show"));
            })}
          >
            <ListItemIcon>
              <PageviewOutlinedIcon />
            </ListItemIcon>
            <ListItemText>
              {translate(viewLabel, { _: viewLabel })}
            </ListItemText>
          </MenuItem>
        )}
        {isEditable() && (
          <MenuItem
            className="actions-menu-item"
            {...menuItemOptions}
            onClick={handleMenuItemClick(() => {
              history.push(linkToRecord(basePathOverride, record.id));
            })}
          >
            <ListItemIcon>
              <EditOutlinedIcon />
            </ListItemIcon>
            <ListItemText>{translate("actions.edit")}</ListItemText>
          </MenuItem>
        )}
        {isActivateOrDeactivateVisible() && <Divider />}
        {isActivateOrDeactivateVisible() &&
          record.status === "Active" && [
            <MenuItem
              key="1"
              className="actions-menu-item"
              {...menuItemOptions}
              onClick={handleMenuItemClick(() => {
                if (confirmDeactivate) {
                  setConfirmOpen(true);
                } else {
                  deactivateRecord(record).then((result) => {
                    dispatch(createCrudStatusChangedAction(result));
                    refresh();
                  });
                }
              })}
            >
              <ListItemIcon>
                <IndeterminateCheckBoxOutlinedIcon />
              </ListItemIcon>
              <ListItemText>{translate("actions.deactivate")}</ListItemText>
            </MenuItem>,
            confirmDeactivate ? (
              <Confirm
                key="2"
                isOpen={isConfirmOpen}
                loading={loading}
                title={confirmTitle}
                content={confirmContent}
                translateOptions={{
                  name: inflection.singularize(
                    translate(`resources.${resource}.forcedCaseName`, {
                      smart_count: 1,
                      _: inflection.humanize(
                        translate(`resources.${resource}.name`, {
                          smart_count: 1,
                          _: inflection.singularize(resource!),
                        }),
                        true
                      ),
                    })
                  ),
                  id: record.id,
                }}
                onConfirm={() => {
                  setConfirmOpen(false);
                  setLoading(true);
                  deactivateRecord(record)
                    .then((result) => {
                      dispatch(createCrudStatusChangedAction(result));
                      refresh();
                    })
                    .finally(() => setLoading(false));
                }}
                onClose={() => {
                  setConfirmOpen(false);
                }}
              />
            ) : null,
          ]}
        {isActivateOrDeactivateVisible() && record.status === "Inactive" && (
          <MenuItem
            className="actions-menu-item"
            {...menuItemOptions}
            onClick={handleMenuItemClick(() => {
              activateRecord(record).then((result) => {
                dispatch(createCrudStatusChangedAction(result));
                refresh();
              });
            })}
          >
            <ListItemIcon>
              <CheckBoxOutlinedIcon />
            </ListItemIcon>
            <ListItemText>{translate("actions.activate")}</ListItemText>
          </MenuItem>
        )}
        {props.children &&
          React.Children.count(props.children) > 0 && [
            (record.status || isEditable() || hasShow) && <Divider key={1} />,
            React.Children.map(props.children, (child, idx) => {
              if (isValidElement(child)) {
                return (
                  <div>
                    {cloneElement(child, {
                      onClick: handleMenuItemClick(child.props.onClick),
                      record,
                      resource,
                      key: idx + 2,
                      ref: undefined,
                    })}
                  </div>
                );
              }
            }),
          ]}
      </Menu>
    </div>
  );
};

const sanitizeRestProps = (props: any) => {
  const { hasList, hasEdit, hasCreate, syncWithLocation, ...rest } = props;

  return rest;
};